/**
 * Core Content Pack.
 *
 * The core pack is always loaded and provides:
 * - Content type registrations (stats, items, skills, etc.)
 * - Base content (health stat, etc.)
 * - Core UI slots and components
 *
 * All other packs depend on core.
 */

import type {
  ActionDefinition,
  ItemDefinition,
  LocationDefinition,
  ShopDefinition,
  SkillDefinition,
  StatDefinition,
} from '@idle-depths/shared';

import type { ContentPack, PackContext } from '../types';

import { CORE_ACTIONS, isActionDefinition } from './content/actions';
import { CORE_ITEMS, isItemDefinition } from './content/items';
import { CORE_LOCATIONS, isLocationDefinition } from './content/locations';
import { CORE_SHOPS, isShopDefinition } from './content/shops';
import { CORE_SKILLS, isSkillDefinition } from './content/skills';
import { CORE_STATS, isStatDefinition } from './content/stats';
import { CORE_SYSTEMS } from './systems';
import { HealthDisplay } from './ui';

/**
 * Core content pack definition.
 */
const CorePack: ContentPack = {
  id: 'core',
  name: 'Idle Depths Core',
  version: '1.0.0',
  entitlement: null, // Always loads
  dependencies: [], // No dependencies

  async register(context: PackContext): Promise<void> {
    const { content, systems, ui } = context;

    // =========================================
    // LAYER 1: Register Content Types
    // =========================================

    // Stats type (health, stamina, etc.)
    content.registerType<StatDefinition>('stats', {
      validate: isStatDefinition,
    });

    // Items type (weapons, armor, resources, etc.)
    content.registerType<ItemDefinition>('items', {
      validate: isItemDefinition,
    });

    // Shops type (location-based shops)
    content.registerType<ShopDefinition>('shops', {
      validate: isShopDefinition,
    });

    // Locations type (game world graph)
    content.registerType<LocationDefinition>('locations', {
      validate: isLocationDefinition,
    });

    // Skills type (progression skills)
    content.registerType<SkillDefinition>('skills', {
      validate: isSkillDefinition,
    });

    // Actions type (skill actions)
    content.registerType<ActionDefinition>('actions', {
      validate: isActionDefinition,
    });

    // Lock content registry - no more type registrations after core
    content.lock();

    // =========================================
    // LAYER 2: Register Content Instances
    // =========================================

    // Register all core stats
    content.registerAll('stats', CORE_STATS);

    // Register all core items
    content.registerAll('items', CORE_ITEMS);

    // Register all core shops
    content.registerAll('shops', CORE_SHOPS);

    // Register all core locations
    content.registerAll('locations', CORE_LOCATIONS);

    // Register all core skills
    content.registerAll('skills', CORE_SKILLS);

    // Register all core actions
    content.registerAll('actions', CORE_ACTIONS);

    // =========================================
    // LAYER 3: Register Game Systems
    // =========================================

    for (const system of CORE_SYSTEMS) {
      systems.register(system);
    }

    // =========================================
    // LAYER 4: Define UI Slots
    // =========================================

    // Character stats slot (health bar, etc.)
    ui.defineSlot({
      id: 'character-stats',
      multiple: true,
      description: 'Character stat displays (health, stamina, etc.)',
    });

    // Sidebar widgets slot
    ui.defineSlot({
      id: 'sidebar-widgets',
      multiple: true,
      description: 'Widgets in the game sidebar',
    });

    // Header actions slot
    ui.defineSlot({
      id: 'header-actions',
      multiple: true,
      description: 'Action buttons in the header',
    });

    // Main content area slot
    ui.defineSlot({
      id: 'main-content',
      multiple: false,
      description: 'Main game content area',
    });

    // Navigation skills slot (for skill list in sidebar)
    ui.defineSlot({
      id: 'navigation-skills',
      multiple: true,
      description: 'Skill navigation items in the sidebar',
    });

    // =========================================
    // LAYER 5: Inject UI Components
    // =========================================

    // Health display in character stats
    ui.inject({
      id: 'core-health-display',
      slot: 'character-stats',
      component: HealthDisplay as React.ComponentType<Record<string, unknown>>,
      order: 0,
    });
  },
};

export default CorePack;
